import React, { useMemo } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';

const SummaryTab = ({ scanData }) => {
  const riskStats = useMemo(() => {
    const stats = {
      High: 0,
      Medium: 0,
      Low: 0,
      None: 0,
      total: 0,
      uniqueHosts: new Set(),
      uniquePorts: new Set(),
      topVulnerabilities: {},
    };

    scanData.forEach((item) => {
      if (item.risk) stats[item.risk]++;
      stats.total++;
      stats.uniqueHosts.add(item.host);
      stats.uniquePorts.add(item.port);
      stats.topVulnerabilities[item.name] = (stats.topVulnerabilities[item.name] || 0) + 1;
    });

    return stats;
  }, [scanData]);

  const topVulnerabilities = useMemo(() => {
    return Object.entries(riskStats.topVulnerabilities)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);
  }, [riskStats]);

  return (
    <div className="summary-dashboard mt-4">
      {/* Risk Overview Cards */}
      <Row className="mb-4">
        <Col lg={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="p-4">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-2 text-uppercase fs-13 fw-semibold">High Risk</p>
                  <h2 className="mb-0" style={{ color: '#dc3545', fontWeight: '600' }}>{riskStats.High}</h2>
                </div>
                <div className="flex-shrink-0">
                  <i className="bx bxs-shield fs-1" style={{ color: '#dc3545' }}></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="p-4">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-2 text-uppercase fs-13 fw-semibold">Medium Risk</p>
                  <h2 className="mb-0" style={{ color: '#ffc107', fontWeight: '600' }}>{riskStats.Medium}</h2>
                </div>
                <div className="flex-shrink-0">
                  <i className="bx bxs-shield fs-1" style={{ color: '#ffc107' }}></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="p-4">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-2 text-uppercase fs-13 fw-semibold">Low Risk</p>
                  <h2 className="mb-0" style={{ color: '#198754', fontWeight: '600' }}>{riskStats.Low}</h2>
                </div>
                <div className="flex-shrink-0">
                  <i className="bx bxs-shield fs-1" style={{ color: '#198754' }}></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="border-0 shadow-sm">
            <CardBody className="p-4">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-2 text-uppercase fs-13 fw-semibold">Total Findings</p>
                  <h2 className="mb-0" style={{ color: '#0dcaf0', fontWeight: '600' }}>{riskStats.total}</h2>
                </div>
                <div className="flex-shrink-0">
                  <i className="bx bxs-shield fs-1" style={{ color: '#0dcaf0' }}></i>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Detailed Statistics */}
      <Row>
        <Col lg={6}>
          <Card className="border-0 shadow-sm">
            <CardBody className="p-4">
              <h5 className="card-title mb-4 fs-16 fw-semibold">Scan Overview</h5>
              <div className="table-responsive">
                <table className="table table-borderless m-0">
                  <tbody>
                    <tr>
                      <td className="py-2 fs-15 text-muted">Total Hosts Scanned</td>
                      <td className="py-2 fs-15 fw-semibold text-end">{riskStats.uniqueHosts.size}</td>
                    </tr>
                    <tr>
                      <td className="py-2 fs-15 text-muted">Total Ports Scanned</td>
                      <td className="py-2 fs-15 fw-semibold text-end">{riskStats.uniquePorts.size}</td>
                    </tr>
                    <tr>
                      <td className="py-2 fs-15 text-muted">Risk Score</td>
                      <td className="py-2 text-end">
                        <span className={`px-3 py-1 rounded-pill fw-semibold fs-12 ${
                          riskStats.High > 0 
                            ? 'text-danger bg-danger bg-opacity-10' 
                            : riskStats.Medium > 0 
                            ? 'text-warning bg-warning bg-opacity-10' 
                            : 'text-success bg-success bg-opacity-10'
                        }`}>
                          {riskStats.High > 0 
                            ? 'Critical' 
                            : riskStats.Medium > 0 
                            ? 'Moderate' 
                            : 'Low'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm">
            <CardBody className="p-4">
              <h5 className="card-title mb-4 fs-16 fw-semibold">Top Vulnerabilities</h5>
              <div className="table-responsive">
                <table className="table m-0">
                  <thead>
                    <tr>
                      <th className="border-0 ps-0 text-muted fs-13">Vulnerability</th>
                      <th className="border-0 text-end pe-0 text-muted fs-13">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topVulnerabilities.map(([name, count], index) => (
                      <tr key={name}>
                        <td className="border-0 ps-0 py-2 fs-15">{name}</td>
                        <td className="border-0 text-end pe-0 py-2">
                          <span className="fw-semibold fs-15" style={{ color: index === 0 ? '#dc3545' : '#495057' }}>
                            {count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SummaryTab;